import { defineSessionSharingSetting } from '@reflex/shared';

/**
 * The org's answer to "may a session started in a Slack channel take
 * replies from everyone in its thread?".
 *
 * A session launched by @-mentioning Reflex in a channel belongs to whoever
 * mentioned it, so a teammate replying in the same thread is writing to
 * someone else's session and is dropped. Turning this on makes the session
 * open an `edit` grant as each teammate joins the thread, which is what
 * "the whole channel is talking to the bot" needs in order to work at all.
 *
 * It is declared, not hard-coded, because it is an org policy rather than a
 * Slack preference: the host stores it on the org, renders the switch on
 * the org Sharing page, and clamps it against the org's own sharing ceiling
 * (`grants: 'edit'` — an edit share lets someone else drive a sandbox
 * provisioned with the launcher's repo access and provider key, so an org
 * that forbids write sharing forbids this too, whatever it opted into
 * earlier). A launcher may narrow a single session from the confirmation
 * card ("Just me"), never widen one.
 *
 * Off by default: an org that never touches the setting keeps sessions
 * private to their launcher, exactly as before the setting existed.
 *
 * Registered on both halves of the plugin — `sessionSharingSettings` on the
 * server definition (storage, validation, the pre-clamped read) and on the
 * web manifest (the switch) — from this one declaration, so the copy an
 * owner reads and the gate the service enforces cannot drift apart.
 */
export const SLACK_CHANNEL_PARTICIPANT_SHARING = defineSessionSharingSetting({
  key: 'slack:channel-participants',
  grants: 'edit',
  defaultEnabled: false,
  copy: {
    title: 'Sessions started from a chat channel',
    description:
      'A session started by mentioning Reflex in a Slack channel belongs to the person who ' +
      'started it, so replies from anyone else are left out of it. Turn this on to let the ' +
      'session take replies from everyone in its thread.',
    switchLabel: 'Share with everyone in the thread',
    whenOn:
      'Each person who replies in the thread is given edit access to that session. The person ' +
      'who started it can still keep an individual session to themselves when they start it.',
    whenOff:
      'Sessions stay private to whoever started them. Other people can ask for access from the ' +
      'session page in Reflex.',
    whenUnavailable:
      'Unavailable while sharing with edit access is off. Joining a session means writing to ' +
      'it, so this setting cannot exceed the limit above.',
    disclosure:
      'Someone with edit access can drive a session whose sandbox holds the starter’s ' +
      'repository access and model provider key.',
  },
});
